import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTheme } from "../Theme/ThemeContext";
import { SkeletonJobDetails } from "../Skeletons";
import {
  fetchJobById,
  fetchAllJobs,
  likeJob,
  unlikeJob,
  applyToJob,
} from "../../apis/JobApis";
import ShareJobButtons from "./ShareJobButtons";
import WhatsAppChannelCard from "../extras/WhatsappHorizontal";
import AmpAd from "../ads/AmpAd";
import SeoMeta from "../SeoMeta";
import { Top20Companies } from "../../assets/constants";
import HorizontalSection from "./HorizontalSection";
import { FaHeart, FaMapMarkerAlt, FaBriefcase, FaCode, FaClock, FaExternalLinkAlt, FaBuilding, FaCheckCircle, FaArrowLeft, FaUsers, FaChartLine } from "react-icons/fa";
import CommentSection from "./CommentSection";
import CoursePromoAd from "../courses/CoursePromoAd";
import { useAuth } from "../Authentication/AuthContext";
import { bulkFetchUsersAdmin } from "../../apis/UserApis";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function JobDetailsPage({ user = {} }) {
  const { id } = useParams();
  const { loggedInUser } = useAuth();
  const { currentTheme } = useTheme();
  const { userId, token } = user;
  const isAdmin = loggedInUser?.role === "admin";
  const [job, setJob] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [appliesCount, setAppliesCount] = useState(0);
  const companies = [job?.companyName, ...Top20Companies];

  const [showAppliesModal, setShowAppliesModal] = useState(false);
  const [appliedUsers, setAppliedUsers] = useState([]);
  const [appliesLoading, setAppliesLoading] = useState(false);
  const [appliesError, setAppliesError] = useState("");
  const [copyEmailsStatus, setCopyEmailsStatus] = useState("idle");
  const [copyEmailsMessage, setCopyEmailsMessage] = useState("");

  useEffect(() => {
    fetchJobById(id).then((data) => {
      setJob(data);
      setLiked(Boolean(data.likes?.[userId]));
      setLikeCount(Object.keys(data.likes || {}).length);
      setAppliesCount(Object.keys(data.applies || {}).length);
    });
  }, [id, userId]);

  if (!job)
    return <SkeletonJobDetails />;

  const handleLike = async () => {
    try {
      if (liked) {
        await unlikeJob(job.id, userId);
        setLikeCount((prev) => prev - 1);
      } else {
        await likeJob(job.id, userId);
        setLikeCount((prev) => prev + 1);
      }
      setLiked(!liked);
    } catch (err) {
      console.error(err);
    }
  };

  const handleApply = async () => {
    try {
      await applyToJob(job.id, userId);
      setAppliesCount((prev) => prev + 1);
      window.open(job.applyUrl, "_blank");
    } catch (err) {
      console.error(err);
    }
  };

  const openApplies = async () => {
    if (!isAdmin) return;
    const applicantIds = Object.keys(job?.applies || {});
    setShowAppliesModal(true);
    setAppliesError("");

    if (applicantIds.length === 0) {
      setAppliedUsers([]);
      return;
    }

    setAppliesLoading(true);
    try {
      const res = await bulkFetchUsersAdmin(applicantIds, token);
      setAppliedUsers(Array.isArray(res?.users) ? res.users : []);
    } catch (e) {
      setAppliesError("Failed to load applicants");
      setAppliedUsers([]);
    } finally {
      setAppliesLoading(false);
    }
  };

  const getApplicantName = (u) => {
    const firstName = String(u?.firstName || "").trim();
    const lastName = String(u?.lastName || "").trim();
    const full = `${firstName} ${lastName}`.trim();
    return full || "-";
  };

  const getApplicantInitials = (u) => {
    const first = String(u?.firstName || "").trim();
    const last = String(u?.lastName || "").trim();
    const a = first ? first[0] : "";
    const b = last ? last[0] : "";
    const initials = `${a}${b}`.toUpperCase();
    return initials || "U";
  };

  const formatApplicantAddress = (address) => {
    if (!address) return "-";
    if (typeof address === "string") {
      const trimmed = address.trim();
      return trimmed || "-";
    }
    try {
      return JSON.stringify(address);
    } catch {
      return "-";
    }
  };

  const getApplicantsEmailsCsv = () => {
    const emails = (appliedUsers || [])
      .map((u) => String(u?.email || "").trim())
      .filter(Boolean);
    return Array.from(new Set(emails)).join("; ");
  };

  const copyTextToClipboard = async (text) => {
    if (!text) return false;

    try {
      if (navigator?.clipboard?.writeText && window?.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fallback below
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-1000px";
      textarea.style.left = "-1000px";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopyApplicantsEmails = async () => {
    const csv = getApplicantsEmailsCsv();
    if (!csv) {
      setCopyEmailsStatus("error");
      setCopyEmailsMessage("No emails found to copy");
      return;
    }

    setCopyEmailsStatus("copying");
    setCopyEmailsMessage("");
    const ok = await copyTextToClipboard(csv);
    setCopyEmailsStatus(ok ? "success" : "error");
    setCopyEmailsMessage(ok ? "Copied emails" : "Failed to copy");

    window.setTimeout(() => {
      setCopyEmailsStatus("idle");
      setCopyEmailsMessage("");
    }, 1500);
  };

  // JobPosting JSON-LD schema
  const jobPostingSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description?.join(" ") || "",
    datePosted: job.createdAt || new Date().toISOString(),
    employmentType: job.type?.toUpperCase() === "INTERNSHIP" ? "INTERN" : "FULL_TIME",
    hiringOrganization: {
      "@type": "Organization",
      name: job.companyName,
      logo: job.companyLogo || "",
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: job.locations?.[0] || "Remote",
        addressCountry: "IN",
      },
    },
    applicantLocationRequirements: job.locations?.includes("Remote")
      ? { "@type": "Country", name: "Remote" }
      : undefined,
    skills: job.primarySkills?.join(", ") || "",
  };

  return (
    <>
      {/* SEO Meta */}
      <SeoMeta
        title={`${job.title} at ${job.companyName} | Code Arena`}
        description={job.description.join(" ").slice(0, 160)}
        url={`${BASE_URL}jobs/${job.id}`}
        image={job.companyLogo || "/logo192.png"}
        keywords={`${job.title}, ${job.companyName}, ${job.primarySkills.join(
          ", "
        )}, IT Jobs`}
        type="article"
        jsonLd={jobPostingSchema}
      />

      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-800">
        {/* Hero Section */}
        <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} text-white`}>
          <div className="ca-container py-8 sm:py-12">
            <Link
              to="/jobs"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium transition-colors mb-6"
            >
              <FaArrowLeft className="w-3 h-3" /> Back to Jobs
            </Link>
            
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Company Logo */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-white shadow-xl flex-shrink-0 p-2">
                <img
                  src={job.companyLogo}
                  alt={`${job.companyName} logo`}
                  className="w-full h-full object-contain"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  {job.isVerified && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm">
                      <FaCheckCircle className="w-3 h-3" /> Verified
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm capitalize">
                    {job.type}
                  </span>
                </div>
                
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
                  {job.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 text-white/90">
                  <span className="inline-flex items-center gap-2">
                    <FaBuilding className="w-4 h-4" /> {job.companyName}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <FaMapMarkerAlt className="w-4 h-4" /> {job.locations.join(", ")}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons - Desktop */}
              <div className="hidden md:flex flex-col gap-3">
                <button
                  onClick={handleApply}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white text-theme-primary font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl"
                >
                  Apply Now <FaExternalLinkAlt className="w-3 h-3" />
                </button>
                <button
                  onClick={handleLike}
                  className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-all ${
                    liked 
                      ? "bg-red-500 text-white" 
                      : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
                  }`}
                >
                  <FaHeart className="w-4 h-4" /> {likeCount} {likeCount === 1 ? "Like" : "Likes"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="ca-container py-8 sm:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Mobile Action Buttons */}
              <div className="flex md:hidden gap-3">
                <button
                  onClick={handleApply}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
                >
                  Apply Now <FaExternalLinkAlt className="w-3 h-3" />
                </button>
                <button
                  onClick={handleLike}
                  className={`px-4 py-3 rounded-xl font-medium transition-all ${
                    liked 
                      ? "bg-red-500 text-white" 
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  <FaHeart className="w-5 h-5" />
                </button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="glass rounded-xl p-4 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
                      <FaBriefcase className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Type</p>
                      <p className="font-semibold text-gray-900 dark:text-white capitalize">{job.type}</p>
                    </div>
                  </div>
                </div>
                <div className="glass rounded-xl p-4 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <FaClock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Experience</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{job.experience.join(", ")}</p>
                    </div>
                  </div>
                </div>
                <div className="glass rounded-xl p-4 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-royal-100 dark:bg-royal-900/30 rounded-lg">
                      <FaMapMarkerAlt className="w-4 h-4 text-royal-600 dark:text-royal-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
                      <p className="font-semibold text-gray-900 dark:text-white truncate">{job.locations[0]}</p>
                    </div>
                  </div>
                </div>
                <div className="glass rounded-xl p-4 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <FaUsers className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Applicants</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{appliesCount}+</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div className="glass rounded-2xl p-6 border border-white/20 dark:border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-lg`}>
                    <FaCode className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-lg font-bold text-neutral-900 dark:text-white">Required Skills</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {job.primarySkills.map((skill, i) => (
                    <span
                      key={i}
                      className="px-4 py-2 bg-gradient-to-r from-accent-50 to-emerald-50 dark:from-accent-900/30 dark:to-emerald-900/30 text-accent-700 dark:text-accent-300 rounded-lg text-sm font-medium border border-accent-100 dark:border-accent-800"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <CoursePromoAd variant="inline" />

              {/* Description */}
              <div className="glass rounded-2xl p-6 sm:p-8 border border-white/20 dark:border-white/10">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-1.5 h-8 bg-gradient-to-b ${currentTheme.colors.gradient} rounded-full`} />
                  <h2 className="text-xl font-bold text-neutral-900 dark:text-white">Job Description</h2>
                </div>
                <div className="prose prose-neutral dark:prose-invert max-w-none">
                  {job.description.map((line, i) => (
                    <p key={i} className="text-neutral-700 dark:text-neutral-300 leading-relaxed mb-4 last:mb-0">
                      {line}
                    </p>
                  ))}
                </div>
              </div>

              <WhatsAppChannelCard />
              
              {/* Apply Section */}
              <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} rounded-2xl p-6 sm:p-8 text-white shadow-lg ${currentTheme.colors.glow}`}>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold mb-1">Ready to Apply?</h3>
                    <p className="text-white/80">Don't miss this opportunity at {job.companyName}</p>
                  </div>
                  <button
                    onClick={handleApply}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 bg-white text-theme-primary font-semibold rounded-xl hover:bg-neutral-50 transition-all shadow-lg"
                  >
                    Apply Now <FaExternalLinkAlt className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <CommentSection job={job} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Share Card */}
              <div className="glass rounded-2xl p-6 border border-white/20 dark:border-white/10">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-4">Share this job</h3>
                <ShareJobButtons job={job} />
              </div>

              {/* Stats Card */}
              {isAdmin && (
                <div className="glass rounded-2xl p-6 border border-white/20 dark:border-white/10">
                  <div className="flex items-center gap-3 mb-4">
                    <FaChartLine className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                    <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Admin Stats</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-neutral-600 dark:text-neutral-300">Total Applies</span>
                      <span className="font-bold text-neutral-900 dark:text-white">{appliesCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-neutral-600 dark:text-neutral-300">Total Likes</span>
                      <span className="font-bold text-neutral-900 dark:text-white">{likeCount}</span>
                    </div>
                  </div>

                  <div className="pt-4 mt-4 border-t border-neutral-100 dark:border-neutral-700">
                    <button
                      onClick={openApplies}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-accent-700 dark:text-accent-300 bg-accent-50 dark:bg-accent-900/30 hover:bg-accent-100 dark:hover:bg-accent-900/50 transition-colors"
                    >
                      <FaUsers className="w-4 h-4" />
                      See Applies
                    </button>
                  </div>
                </div>
              )}

              {/* Companies Card */}
              <div className="glass rounded-2xl p-6 border border-white/20 dark:border-white/10">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-white mb-4">Popular Companies</h3>
                <div className="flex flex-wrap gap-2">
                  {companies.filter(Boolean).slice(0, 10).map((company) => (
                    <Link
                      to={`/jobs/company/${encodeURIComponent(company)}`}
                      key={company}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        job.companyName === company
                          ? `bg-gradient-to-r ${currentTheme.colors.gradient} text-white shadow-md`
                          : "bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-600"
                      }`}
                    >
                      {company}
                    </Link>
                  ))}
                </div>
              </div>

              <AmpAd />
            </div>
          </div>
        </div>
      </div>

      <PostsSection company={job.companyName} skill={job.primarySkills[0]} />

      {/* Applies Modal (Admin only) */}
      {showAppliesModal && isAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6">
          <div className="w-full max-w-2xl glass rounded-3xl shadow-2xl border border-white/20 dark:border-white/10 overflow-hidden">
            <div className="px-6 py-5 bg-gradient-to-r from-accent-600 to-emerald-600 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Applicants</h3>
                <p className="text-white/80 text-sm">{appliesCount} total applies</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyApplicantsEmails}
                  disabled={appliesLoading || appliedUsers.length === 0 || copyEmailsStatus === "copying"}
                  className="hidden sm:inline-flex items-center justify-center px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
                  title="Copy all applicant emails (comma-separated)"
                >
                  {copyEmailsStatus === "copying" ? "Copying..." : "Copy emails"}
                </button>
              <button
                onClick={() => setShowAppliesModal(false)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
                aria-label="Close"
              >
                <span className="text-xl leading-none">×</span>
              </button>
              </div>
            </div>

            <div className="p-6">
              {!appliesLoading && !appliesError && appliedUsers.length > 0 && (
                <div className="sm:hidden mb-4">
                  <button
                    type="button"
                    onClick={handleCopyApplicantsEmails}
                    disabled={copyEmailsStatus === "copying"}
                    className={`w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-white text-sm font-semibold`}
                  >
                    {copyEmailsStatus === "copying" ? "Copying..." : "Copy emails"}
                  </button>
                </div>
              )}

              {copyEmailsMessage ? (
                <div
                  className={`mb-4 text-sm font-medium ${
                    copyEmailsStatus === "success"
                      ? "text-green-700 dark:text-green-300"
                      : copyEmailsStatus === "error"
                      ? "text-red-700 dark:text-red-300"
                      : "text-gray-700 dark:text-gray-300"
                  }`}
                >
                  {copyEmailsMessage}
                </div>
              ) : null}

              {appliesLoading && (
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  Loading applicants...
                </div>
              )}

              {!appliesLoading && appliesError && (
                <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                  {appliesError}
                </div>
              )}

              {!appliesLoading && !appliesError && appliedUsers.length === 0 && (
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm">
                  No applicants yet.
                </div>
              )}

              {!appliesLoading && !appliesError && appliedUsers.length > 0 && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-semibold">{appliedUsers.length}</span> applicant{appliedUsers.length === 1 ? "" : "s"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Tip: click an email to open mail client
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <div className="max-h-[60vh] overflow-auto">
                      <table className="min-w-full text-left">
                        <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-600">
                          <tr>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">#</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Candidate</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Email</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Address</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800">
                          {appliedUsers.map((u, idx) => {
                            const key = u.id || u._id || u.userId || u.email || `${idx}`;
                            const name = getApplicantName(u);
                            const initials = getApplicantInitials(u);
                            const email = u?.email ? String(u.email) : "";
                            const address = formatApplicantAddress(u?.address);

                            return (
                              <tr key={key} className="border-b border-gray-50 dark:border-gray-700 hover:bg-gray-50/70 dark:hover:bg-gray-700/50 transition-colors">
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 align-top">{idx + 1}</td>
                                <td className="px-4 py-3 align-top">
                                  <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-accent-600 to-emerald-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                      {initials}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{name}</p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {u?.userId || u?.id || u?._id || ""}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 align-top">
                                  {email ? (
                                    <a
                                      href={`mailto:${email}`}
                                      className="text-sm font-medium text-accent-700 dark:text-accent-400 hover:text-accent-800 dark:hover:text-accent-300 hover:underline break-all"
                                    >
                                      {email}
                                    </a>
                                  ) : (
                                    <span className="text-sm text-gray-700 dark:text-gray-300">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 align-top">
                                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words max-w-[26rem]">
                                    {address}
                                  </p>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PostsSection({ company, skill }) {
  const [companyJobs, setCompanyJobs] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [skillJobs, setSkillJobs] = useState([]);

  useEffect(() => {
    fetchAllJobs({ company: company, size: 4 }).then((data) => {
      setCompanyJobs(data.jobs);
    });
  }, [company]);

  useEffect(() => {
    fetchAllJobs({ size: 4 }).then((data) => {
      setRecentJobs(data.jobs);
    });
  }, [company]);

  useEffect(() => {
    fetchAllJobs({ skill: skill, size: 4 }).then((data) => {
      setSkillJobs(data.jobs);
    });
  }, [skill]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <HorizontalSection
        title={`Other jobs from ${company}`}
        posts={companyJobs}
      />
      <HorizontalSection title="Recent jobs" posts={recentJobs} />
      <HorizontalSection
        title={`Similar skill jobs - ${skill}`}
        posts={skillJobs}
      />
    </div>
  );
}
